import React, { useState, useEffect, useCallback } from "react";
import ReactFlow, { Background, Controls, MiniMap, addEdge, useNodesState, useEdgesState } from "reactflow";
import "reactflow/dist/style.css";
import { api } from "@/lib/api";
import { PageHeader, Btn, Input, Modal, Field, useDirtyGuard, ConfirmClose } from "@/components/erp/ui";
import { toast } from "sonner";
import { Plus, Save, Trash2, GripVertical, Play, Sparkles } from "lucide-react";

const PALETTE_FALLBACK = [
  { label: "Booking / Order", color: "#0ea5e9" },
  { label: "Appraisal", color: "#6366f1" },
  { label: "Design", color: "#8b5cf6" },
  { label: "Material Prep", color: "#14b8a6" },
  { label: "3D Printing", color: "#0284c7" },
  { label: "QC Check", color: "#f59e0b" },
  { label: "Finishing", color: "#ec4899" },
  { label: "Assembly", color: "#10b981" },
  { label: "Packing", color: "#64748b" },
  { label: "Delivery", color: "#16a34a" },
  { label: "Decision / Branch", color: "#ef4444" },
];

const nodeStyle = (color) => ({ background: color, color: "#fff", border: "none", borderRadius: 12, padding: "10px 16px", fontSize: 12, fontWeight: 600, width: 160, boxShadow: "0 4px 12px rgba(2,6,23,.15)" });

export default function Workflow() {
  const [list, setList] = useState([]);
  const [palette, setPalette] = useState(PALETTE_FALLBACK);
  useEffect(() => { api.get("/settings/production-nodes").then(({ data }) => setPalette((data || []).filter((n) => n.active !== false))).catch(() => {}); }, []);
  const [active, setActive] = useState(null);
  const [name, setName] = useState("");
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [runOpen, setRunOpen] = useState(false);
  const [runForm, setRunForm] = useState({ product: "", quantity: 1 });
  const g = useDirtyGuard();
  const forceClose = () => { g.clear(); setRunOpen(false); setRunForm({ product: "", quantity: 1 }); };
  const tryClose = () => g.attemptClose(runForm, forceClose);

  const loadList = () => api.get("/workflows").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { loadList(); }, []);

  const onConnect = useCallback((params) => {
    const src = nodes.find((n) => n.id === params.source);
    let label = "";
    if (src && /decision|branch/i.test(src.data?.label || "")) {
      label = window.prompt("Label cabang (mis. PASS / REWORK):", "PASS") || "";
    }
    setEdges((eds) => addEdge({ ...params, label, animated: true, labelStyle: { fontSize: 11, fontWeight: 700 }, style: { stroke: label ? "#f59e0b" : "#94a3b8", strokeWidth: 2 } }, eds));
  }, [setEdges, nodes]);

  const addNode = (item) => {
    const id = `n_${Date.now()}_${Math.floor(Math.random() * 999)}`;
    setNodes((nds) => nds.concat({ id, data: { label: item.label }, position: { x: 80 + Math.random() * 260, y: 60 + Math.random() * 260 }, style: nodeStyle(item.color) }));
  };

  const openWf = (wf) => {
    setActive(wf); setName(wf.name);
    setNodes((wf.nodes || []).map((n, i) => ({ ...n, position: (n.position && typeof n.position.x === "number" && typeof n.position.y === "number") ? n.position : { x: 80 + (i % 4) * 200, y: 60 + Math.floor(i / 4) * 120 } })));
    setEdges(wf.edges || []);
  };

  const newWf = async () => {
    const { data } = await api.post("/workflows", { name: "Alur Produksi Baru", nodes: [], edges: [] });
    toast.success("Workflow dibuat"); await loadList(); openWf(data);
  };

  const newStd = async () => {
    const { data } = await api.post("/workflows/standard");
    toast.success("Workflow standar FOMU dibuat sesuai alur sistem"); await loadList(); openWf(data);
  };

  const save = async () => {
    if (!active) return;
    await api.put(`/workflows/${active.id}`, { name, description: active.description || "", nodes, edges });
    toast.success("Workflow tersimpan"); loadList();
  };

  const del = async () => {
    if (!active || !window.confirm("Hapus workflow?")) return;
    await api.delete(`/workflows/${active.id}`); setActive(null); setNodes([]); setEdges([]); setName(""); loadList();
  };

  const run = async () => {
    if (!runForm.product) { toast.error("Nama produk wajib"); return; }
    const { data } = await api.post(`/workflows/${active.id}/run`, { product: runForm.product, quantity: +runForm.quantity });
    toast.success(`Work Order ${data.code} dibuat dari workflow (${data.stages.length} tahap)`);
    setRunOpen(false); setRunForm({ product: "", quantity: 1 });
  };

  return (
    <div>
      <PageHeader title="Workflow Builder" subtitle="Susun alur kerja produksi secara visual — drag node, tarik garis, tanpa koding." action={<div className="flex gap-2"><Btn variant="ghost" data-testid="std-workflow-btn" onClick={newStd}><Sparkles className="w-4 h-4" /> Workflow Standar FOMU</Btn><Btn data-testid="new-workflow-btn" onClick={newWf}><Plus className="w-4 h-4" /> Workflow</Btn></div>} />
      <div className="grid lg:grid-cols-[220px_1fr] gap-4">
        {/* Left: list + palette */}
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-3">
            <div className="text-xs font-mono text-slate-500 px-1 mb-2">SAVED WORKFLOWS</div>
            {list.length === 0 && <div className="text-xs text-slate-400 px-1 py-2">Belum ada. Klik + Workflow.</div>}
            {list.map((wf) => (
              <button key={wf.id} data-testid={`wf-item-${wf.id}`} onClick={() => openWf(wf)} className={`w-full text-left px-3 py-2 rounded-lg text-sm mb-1 ${active?.id === wf.id ? "bg-slate-900 text-white" : "hover:bg-slate-100"}`}>
                {wf.name}<div className={`text-[10px] font-mono ${active?.id === wf.id ? "text-slate-400" : "text-slate-400"}`}>{(wf.nodes || []).length} nodes</div>
              </button>
            ))}
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-3">
            <div className="text-xs font-mono text-slate-500 px-1 mb-2">PRODUCTION NODES</div>
            <div className="space-y-1.5">
              {palette.map((p) => (
                <button key={p.label} data-testid={`palette-${p.label}`} disabled={!active} onClick={() => addNode(p)} className="w-full flex items-center gap-2 text-left text-sm px-2.5 py-2 rounded-lg hover:bg-slate-100 disabled:opacity-40">
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ background: p.color }} />
                  <GripVertical className="w-3.5 h-3.5 text-slate-300" /> {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Canvas */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col" style={{ height: "640px" }}>
          {active ? (
            <>
              <div className="flex items-center gap-2 p-3 border-b border-slate-200">
                <Input data-testid="wf-name" value={name} onChange={(e) => setName(e.target.value)} className="max-w-xs" />
                <Btn data-testid="save-workflow-btn" onClick={save}><Save className="w-4 h-4" /> Simpan</Btn>
                <Btn variant="accent" data-testid="run-workflow-btn" onClick={() => { const f = { product: "", quantity: 1 }; setRunForm(f); g.snapshot(f); setRunOpen(true); }}><Play className="w-4 h-4" /> Run</Btn>
                <Btn variant="danger" onClick={del}><Trash2 className="w-4 h-4" /></Btn>
                <span className="ml-auto text-xs text-slate-400 font-mono">{nodes.length} nodes · {edges.length} edges</span>
              </div>
              {nodes.some((n) => n.data?.desc) && <div className="flex flex-wrap gap-1.5 px-3 py-2 border-b border-slate-100 text-[11px]" data-testid="wf-steps">{nodes.map((n, i) => n.data?.desc && <span key={n.id} className="px-2 py-1 rounded-lg bg-slate-50 border border-slate-200"><b>{i + 1}. {n.data.label}</b> — {n.data.desc}</span>)}</div>}
              <div className="flex-1" data-testid="wf-canvas">
                <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect} fitView proOptions={{ hideAttribution: true }}>
                  <Background color="#cbd5e1" gap={20} />
                  <Controls />
                  <MiniMap pannable zoomable />
                </ReactFlow>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
              <p className="text-sm">Pilih atau buat workflow untuk mulai menyusun alur produksi.</p>
              <Btn className="mt-4" onClick={newWf}><Plus className="w-4 h-4" /> Workflow Baru</Btn>
            </div>
          )}
        </div>
      </div>
      <Modal open={runOpen} onClose={tryClose} title="Run Workflow → Work Order"
        footer={<><Btn variant="ghost" onClick={tryClose}>Batal</Btn><Btn data-testid="confirm-run-btn" onClick={run}>Buat Work Order</Btn></>}>
        <p className="text-sm text-slate-500 mb-4">Work Order baru akan dibuat dengan tahap produksi sesuai node pada workflow ini (urut mengikuti koneksi).</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Field label="Nama Produk *"><Input data-testid="run-product" value={runForm.product} onChange={(e) => setRunForm({ ...runForm, product: e.target.value })} /></Field></div>
          <Field label="Quantity"><Input type="number" value={runForm.quantity} onChange={(e) => setRunForm({ ...runForm, quantity: e.target.value })} /></Field>
        </div>
      </Modal>
      <ConfirmClose open={g.confirmOpen} testid="runwf-confirm-close" onCancel={() => g.setConfirmOpen(false)} onDiscard={forceClose} onSave={async () => { g.setConfirmOpen(false); await run(); }} />
    </div>
  );
}
